import React, { useMemo } from 'react';
import { IRankingTable } from './interfaces';
import { Avatar, Box, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { alpha } from '@mui/material/styles';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { BORDER_COLOR } from '../../../../../constants';
import { PositionChangeIndicator } from './components/PositionChangeIndicator';
import { useTranslation } from 'react-i18next';
import { BadgeListCell } from '@components/data-table';
import { mapWeaponGroupsToBadgeItems } from '@helpers/weaponGroups';

const HIGHLIGHTED_ROW_CLASS = 'ranking-table-row--highlighted';
const UNRANKED_ROW_CLASS = 'ranking-table-row--unranked';

const WEAPON_GROUP_COLUMN_WIDTH = 220;
const WEAPON_GROUP_COLUMN_WIDTH_COMPACT = 168;
const SUBMITTED_COUNT_COLUMN_WIDTH = 180;
const SUBMITTED_COUNT_COLUMN_WIDTH_COMPACT = 112;
const NAME_COLUMN_WIDTH = 250;
const NAME_COLUMN_MIN_WIDTH = 160;
const SCORE_COLUMN_WIDTH = 150;
const SCORE_COLUMN_MIN_WIDTH = 96;
const WEAPON_GROUP_COLUMN_MIN_WIDTH = 120;
const WEAPON_GROUP_COLUMN_MIN_WIDTH_COMPACT = 112;
const SUBMITTED_COUNT_COLUMN_MIN_WIDTH = 120;
const SUBMITTED_COUNT_COLUMN_MIN_WIDTH_COMPACT = 96;

const formatRankCell = (rank: number | null): string => {
  return rank === null ? '–' : String(rank);
};

const formatScoreCell = (score: number | null): string => {
  return score === null ? '–' : String(score);
};

const formatSubmittedCountCell = (
  count: number,
  minCountingScores?: number
): string => {
  if (minCountingScores === undefined) {
    return String(count);
  }

  return `${count} / ${minCountingScores}`;
};

export const RankingTable: React.FC<IRankingTable> = ({
  rankingData,
  submittedCountColumnLabel = 'Ingeleverde Scores',
  submittedCountColumnTooltip,
  scoreColumnLabel = 'Gem. Score',
  weaponGroupColumnLabel,
  minCountingScores,
  compactMetricColumns = false,
  disableColumnSorting = false,
  highlightedMemberSlug,
}): JSX.Element => {
  const weaponGroupColumnWidth = compactMetricColumns
    ? WEAPON_GROUP_COLUMN_WIDTH_COMPACT
    : WEAPON_GROUP_COLUMN_WIDTH;
  const submittedCountColumnWidth = compactMetricColumns
    ? SUBMITTED_COUNT_COLUMN_WIDTH_COMPACT
    : SUBMITTED_COUNT_COLUMN_WIDTH;
  const weaponGroupColumnMinWidth = compactMetricColumns
    ? WEAPON_GROUP_COLUMN_MIN_WIDTH_COMPACT
    : WEAPON_GROUP_COLUMN_MIN_WIDTH;
  const submittedCountColumnMinWidth = compactMetricColumns
    ? SUBMITTED_COUNT_COLUMN_MIN_WIDTH_COMPACT
    : SUBMITTED_COUNT_COLUMN_MIN_WIDTH;
  const { t } = useTranslation();

  const resolvedWeaponGroupColumnLabel =
    weaponGroupColumnLabel ??
    t('competitions:portal.yourScores.columns.weaponGroup');

  const showWeaponGroupColumn = useMemo(
    () => rankingData.some((row) => row.weaponGroups.length > 0),
    [rankingData]
  );

  const getMedal = (rank: number | null): string => {
    if (rank === null) {
      return '';
    }

    switch (rank) {
      case 1:
        return '🥇';
      case 2:
        return '🥈';
      case 3:
        return '🥉';
      default:
        return '';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'position',
      headerName: t('competitions:portal.ranking.columns.position'),
      width: 80,
      renderCell: (params) => (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}
        >
          <Typography variant="body2">
            {formatRankCell(params.row.rank)}
          </Typography>
          {params.row.rank !== null && (
            <PositionChangeIndicator
              positionChange={params.row.positionChange}
              rank={params.row.rank}
              previousRank={params.row.previousRank}
            />
          )}
        </Box>
      ),
      sortable: false,
      resizable: false,
    },
    {
      field: 'name',
      headerName: t('competitions:portal.ranking.columns.participant'),
      width: NAME_COLUMN_WIDTH,
      minWidth: NAME_COLUMN_MIN_WIDTH,
      flex: 1,
      resizable: true,
      renderCell: (params) => (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}
        >
          <Avatar
            src={params.row.avatarSource}
            alt={params.row.name}
            sx={{
              width: '30px',
              height: '30px',
            }}
          />
          <Typography variant="body2">
            {params.row.name} {getMedal(params.row.rank)}
          </Typography>
        </Box>
      ),
      sortable: false,
    },
    ...(showWeaponGroupColumn
      ? [
          {
            field: 'weaponGroups',
            headerName: resolvedWeaponGroupColumnLabel,
            width: weaponGroupColumnWidth,
            minWidth: weaponGroupColumnMinWidth,
            sortable: false,
            resizable: true,
            renderCell: (params) => (
              <BadgeListCell
                items={mapWeaponGroupsToBadgeItems(params.row.weaponGroups ?? [])}
              />
            ),
          } as GridColDef,
        ]
      : []),
    {
      field: 'totalSubmitted',
      headerName: submittedCountColumnLabel,
      width: submittedCountColumnWidth,
      minWidth: submittedCountColumnMinWidth,
      align: 'center',
      headerAlign: 'center',
      resizable: true,
      sortable: !disableColumnSorting,
      renderHeader: submittedCountColumnTooltip
        ? () => (
            <Tooltip title={submittedCountColumnTooltip}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'help',
                }}
              >
                <Typography variant="body2" component="span" fontWeight="medium">
                  {submittedCountColumnLabel}
                </Typography>
                <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.7 }} />
              </Box>
            </Tooltip>
          )
        : undefined,
      renderCell: (params) =>
        formatSubmittedCountCell(params.row.totalSubmitted, minCountingScores),
    },
    {
      field: 'totalScore',
      headerName: scoreColumnLabel,
      width: SCORE_COLUMN_WIDTH,
      minWidth: SCORE_COLUMN_MIN_WIDTH,
      align: 'center',
      headerAlign: 'center',
      resizable: true,
      sortable: !disableColumnSorting,
      renderCell: (params) => formatScoreCell(params.row.totalScore),
    },
  ];

  return (
    <DataGrid
      rows={rankingData}
      getRowId={(row) => row.id ?? String(row.rank)}
      columns={columns}
      pageSizeOptions={[5]}
      disableRowSelectionOnClick
      disableColumnSorting={disableColumnSorting}
      disableColumnMenu={true}
      hideFooter
      hideFooterPagination
      getRowClassName={(params) => {
        if (highlightedMemberSlug && params.id === highlightedMemberSlug) {
          return HIGHLIGHTED_ROW_CLASS;
        }

        if (params.row.rank === null) {
          return UNRANKED_ROW_CLASS;
        }

        return '';
      }}
      sx={{
        bgcolor: '#FFF',
        borderColor: BORDER_COLOR,
        borderRadius: '0',
        borderWidth: '.5px',
        [`& .${HIGHLIGHTED_ROW_CLASS}`]: {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          boxShadow: (theme) =>
            `inset 3px 0 0 ${theme.palette.primary.main}`,
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14),
          },
        },
        [`& .${UNRANKED_ROW_CLASS}`]: {
          color: 'text.secondary',
          '& .MuiTypography-root': {
            color: 'text.secondary',
          },
        },
      }}
    />
  );
};
